import type {
  AnsweredConsentSignals,
  ConsentSignalKey,
  ConsentSignals,
} from '@/lib/consent/consent-signals';

import type { AnalyticsAdapter, CaptureOptions } from './types';

import { getOrCreateDataLayer } from './data-layer';
import { loadGtmContainer } from './gtm-container-loader';
import { stripPiiKeys } from './strip-pii';

/**
 * Google Tag Manager dataLayer adapter (PX-262).
 *
 * Fan-out destination for ad/marketing tags: translates the client's
 * semantic events into structured `window.dataLayer` pushes for a GTM
 * container to consume:
 *
 *   track    → { event: 'rio.track',    rio: { name, eventId, properties } }
 *   page     → { event: 'rio.page',     rio: { name, eventId, properties } }
 *   identify → { event: 'rio.identify', rio: { userId, traits } }
 *   reset    → { event: 'rio.reset',    rio: { userId: null, traits: nulls } }
 *
 * `track` and `page` each emit a triggerless clear entry ahead of the one
 * shown above — see the recursive-merge section.
 *
 * `name` is the AnalyticsClient's dotted taxonomy (`track.project.created`,
 * `ui.button.clicked`) passed through unchanged — event-to-tag mapping is
 * GTM-container configuration, not adapter code. `eventId` is the
 * client-generated UUID shared with every other destination, hoisted out
 * of `properties` so container tags can map it as a flat Data Layer
 * variable (`rio.eventId`) for client/server conversion dedup (PX-270).
 *
 * ## The container script is loaded next door
 *
 * This adapter is only the feed; `gtm-container-loader.ts` puts the
 * container that consumes it on the page, and the factory below calls it.
 * The two stay separable because pushing to `window.dataLayer` without
 * gtm.js is the standard pattern — entries queue in a plain array and the
 * container replays them if and when it loads — which is what happens
 * outside production, where the loader deliberately does nothing.
 *
 * ## Consent
 *
 * This adapter writes no Consent Mode state. Consent Mode has exactly one
 * writer on this page — `consent-mode-bootstrap.ts` — and a container's
 * per-tag consent checks read that, so publishing consent onto the
 * dataLayer here would put a second, divergable copy of it on the same
 * array for a container author to configure against by mistake.
 *
 * What is left for this adapter to decide is whether the feed carries
 * data at all, and that is a decision Consent Mode cannot make for it:
 * Consent Mode withholds *tags*, not dataLayer entries, and an entry
 * pushed under denied consent sits in a global array that every custom
 * tag in the container can read regardless. So `setConsent` gates locally
 * — see {@link GTM_AD_CONSENT_SIGNALS} for why on the ad signals rather
 * than `analytics_storage`.
 *
 * The gate has three positions, not two, because "not told yet" is a
 * different answer from "denied": React runs a child's mount effect
 * before its parent's, so a route that emits on mount reaches this
 * adapter before `AnalyticsProvider`'s effect has resolved consent and
 * called `setConsent`. Those events wait (see
 * {@link MAX_PENDING_EVENTS}) and a granting `setConsent` replays them,
 * a deliberately denying one drops them. Reading the unresolved state as
 * denied instead costs the first event of every landing — permanently,
 * since the call sites that emit on mount guard against emitting twice —
 * to visitors whose stored consent grants ads.
 *
 * A deny-all `setConsent` is not by itself the answer that ends the wait.
 * A visitor who has never chosen has every ad category `unset`, and gtag
 * has no third state, so what reaches this adapter is a full deny — which
 * is why `setConsent` takes the answered set beside the signals and stays
 * unresolved when the denial is only the region policy's default. Held
 * events then survive until the visitor decides, and the accept that
 * follows on a first visit replays them instead of finding an empty
 * buffer. What waiting must never become is transmission: an unresolved
 * gate pushes nothing, so a visitor who never answers has events retained
 * in memory for the life of the page and sent nowhere.
 *
 * `reset()` is the one exception — it pushes only nulls to clear
 * previously pushed identity, so it is allowed regardless of consent.
 *
 * ## PII
 *
 * Same discipline as the GA adapter: event properties pass through the
 * shared PII blocklist, and identify traits pass through a strict
 * allowlist — never email, name, or domain. Widening the allowlist is a
 * privacy-policy decision, not a convenience edit.
 *
 * ## Recursive merge: clear, then push
 *
 * GTM's data model merges pushed objects recursively and persists values
 * across pushes: a key left out of a push keeps the value an earlier push
 * gave it, and a nested object is merged into rather than replaced —
 * pushing `properties: { color: 'red' }` leaves the previous event's
 * `properties.plan` in the model beside it. A push cannot undo its own
 * merge, so each event goes out as two entries: a clear that nulls every
 * key in `GTM_PER_EVENT_KEYS`, then the event's values. The clear carries
 * no `event` key, so it updates the model without firing any trigger.
 *
 * By the time a tag runs on the `rio.track` / `rio.page` trigger,
 * `rio.name`, `rio.eventId` and `rio.properties` hold this event's values
 * and nothing else. `rio.eventId` is why that is worth two pushes: it is
 * the client/server conversion dedup key ad platforms match on, so a
 * lingering value would dedup a conversion against the wrong event.
 *
 * Identity (`rio.userId`, `rio.traits`) sits deliberately outside the
 * per-event set, untouched by the clear: `identify` pushes it and the
 * merge is what keeps it readable by the events that follow, until
 * `reset` nulls it.
 */

/**
 * Allowlist of identify-trait fields permitted to flow to the dataLayer.
 * Mirrors the GA adapter's `GA_USER_PROPERTY_ALLOWLIST` and exists for
 * the same reason: anything pushed here is readable by every tag in the
 * container, so it fails closed — new traits added to the identify
 * payload will silently NOT flow to GTM unless listed here.
 */
const GTM_IDENTIFY_TRAIT_ALLOWLIST = ['side', 'organizationId'] as const;

/**
 * The Consent Mode signals that govern this feed: the ad purposes, and
 * deliberately not `analytics_storage`.
 *
 * This destination exists for ad/marketing tags and product-analytics
 * destinations are never added inside the container, so analytics consent
 * says nothing about whether it may carry data — gating on it would feed
 * ad tags the events of a visitor who granted analytics and refused ads.
 *
 * Any single ad grant opens the feed, because either ad category leaves
 * some container tag with consented work to do (`adMeasurement` →
 * `ad_storage` + `ad_user_data`, `adPersonalization` →
 * `ad_personalization`). Which of those tags may then fire is the
 * container's decision through its consent checks, which the published
 * Consent Mode state answers per purpose; this adapter cannot make that
 * distinction because it does not know which tag reads which key.
 */
const GTM_AD_CONSENT_SIGNALS = [
  'ad_storage',
  'ad_user_data',
  'ad_personalization',
] as const satisfies readonly ConsentSignalKey[];

/**
 * The `rio.*` keys that describe one event, rather than the identity that
 * spans events. Both of an event's entries — the clear and the values —
 * are built from this set, so neither can cover a key the other misses.
 *
 * This is the one place the set is named: adding a key here is a type
 * error at the `pushEvent` call site until that event supplies a value
 * for it, so a new per-event key cannot ship without its clear.
 */
const GTM_PER_EVENT_KEYS = ['name', 'eventId', 'properties'] as const;

type GtmPerEventKey = (typeof GTM_PER_EVENT_KEYS)[number];

/**
 * How many pre-decision events are held before the rest are dropped.
 *
 * Nothing guarantees a decision ever arrives: a visitor who never answers
 * the banner leaves the gate unresolved for as long as the page is open,
 * and so does a surface that renders the client without
 * `AnalyticsProvider`'s effect ever running. What the window has to cover
 * is a landing's own events — the mount effects before the provider's
 * effect, then its pageview — not a whole session's browsing, so a small
 * bound loses nothing real and keeps the retention obviously finite.
 */
const MAX_PENDING_EVENTS = 20;

export class GtmDataLayerAdapter implements AnalyticsAdapter {
  readonly name = 'gtm' as const;
  private readonly initialized: boolean;
  private consent: 'unresolved' | 'granted' | 'denied' = 'unresolved';
  /**
   * Events that arrived before the visitor's consent was known, as the
   * pushes they will make. Held rather than dropped — see the Consent
   * section above. Nothing in here reaches the dataLayer until a grant
   * drains it.
   */
  private pending: (() => void)[] = [];

  /**
   * @param requestContainer Asks for the container script again — see
   * {@link GtmDataLayerAdapter.page}. Idempotent, so calling it on a page
   * that already has one costs a `querySelector`.
   */
  constructor(private readonly requestContainer: () => void) {
    // SSR guard, same posture as the GA adapter: no document, no
    // dataLayer, adapter reports not-ready and every method no-ops.
    this.initialized = globalThis.document !== undefined;
    if (this.initialized) getOrCreateDataLayer();
  }

  /**
   * Feeds a pageview to the container, and asks for the container itself
   * first.
   *
   * The retry is what keeps the credential-bearing-path guard from
   * outliving the page it protects. The loader runs once per page from the
   * factory below, but the client is built once per *tab* — so a visitor
   * who arrives on a token URL from an email link would otherwise spend
   * the rest of the session with no container, and four of the five token
   * routes leave by client-side navigation (reset success to login,
   * expired reset to forgot-password, work-email error to settings,
   * verify-email error to login). Losing every pixel for the remainder of
   * a session that began with a password reset is worse than the leak the
   * guard closes.
   *
   * A pageview is the right moment to ask again because the client drops
   * the event entirely on a credential-bearing path, so reaching here
   * means the current path is one the container may load on. The loader
   * re-checks the path regardless, so injection stays path-dependent
   * rather than resting on when this adapter happened to be built.
   */
  page(
    name: string,
    properties?: Record<string, unknown>,
    _options?: CaptureOptions,
  ) {
    this.requestContainer();
    // `options.immediate` has no meaning here: a dataLayer push is a
    // synchronous array write; delivery transport belongs to whichever
    // tags the container fires.
    this.pushEvent('rio.page', name, properties);
  }

  track(
    event: string,
    properties?: Record<string, unknown>,
    _options?: CaptureOptions,
  ) {
    this.pushEvent('rio.track', event, properties);
  }

  trackException(
    _error: unknown,
    _additionalProperties?: Record<string, unknown>,
    _options?: CaptureOptions,
  ) {
    // Intentional no-op: this is an ads/marketing destination. Error
    // tracking lives in PostHog (and GA's native exception event).
  }

  identify(userId: string, traits?: Record<string, unknown>) {
    // Not held when consent is unresolved, unlike an event: identity is
    // only ever pushed from the provider's consent effect, and replaying
    // it after the `reset()` in that same effect would restore identity
    // the reset exists to clear.
    if (!this.canPush()) return;
    getOrCreateDataLayer().push({
      event: 'rio.identify',
      rio: { userId, traits: filterTraitsForGtm(traits) },
    });
  }

  reset() {
    if (!this.initialized) return;
    // Allowed under denied consent: the entry carries only nulls,
    // clearing any previously pushed identity so later anonymous hits
    // aren't mis-attributed. Iterates the allowlist so a new entry can't
    // drift between `identify` and `reset` (same shape as GA's reset).
    const traits: Record<string, null> = {};
    for (const key of GTM_IDENTIFY_TRAIT_ALLOWLIST) traits[key] = null;
    getOrCreateDataLayer().push({
      event: 'rio.reset',
      rio: { userId: null, traits },
    });
  }

  isReady(): boolean {
    return this.initialized;
  }

  /**
   * The consent entry point, and the only thing that can open this feed.
   * Reads the ad signals and which of them the visitor answered; writes no
   * Consent Mode state of its own.
   */
  setConsent(
    signals: ConsentSignals,
    answeredSignals?: AnsweredConsentSignals,
  ) {
    const isAdConsentGranted = GTM_AD_CONSENT_SIGNALS.some(
      (signal) => signals[signal] === 'granted',
    );
    if (isAdConsentGranted) {
      this.consent = 'granted';
      const pending = this.pending;
      this.pending = [];
      for (const push of pending) push();
      return;
    }
    // A denial the visitor gave answers the wait, and what waited is
    // dropped. A denial that is only the default for a category they have
    // not been asked about answers nothing, so the gate goes back to
    // unresolved: it keeps holding — bounded, in memory, pushing nothing —
    // rather than discarding the events an accept is about to want. Any
    // one answered ad signal counts, matching the grant rule above, which
    // opens on any one grant.
    const isDenialAnswered = GTM_AD_CONSENT_SIGNALS.some(
      (signal) => answeredSignals?.has(signal) ?? true,
    );
    this.consent = isDenialAnswered ? 'denied' : 'unresolved';
    if (isDenialAnswered) this.pending = [];
  }

  /**
   * Analytics-only shorthand against an ad destination, so it deliberately
   * leaves the gate where it is: a caller that means "analytics on" has
   * said nothing about ads, and must not be able to open this feed.
   * Granting is `setConsent`'s job.
   */
  optIn() {
    // Intentionally does not open the gate — see above.
  }

  /**
   * Closes the gate, unlike its counterpart. The asymmetry is the
   * consent module's deny-wins reading (`reconcileChoicesDenyWins`): a
   * withdrawal is always safe to over-apply, a grant never is.
   */
  optOut() {
    this.consent = 'denied';
    this.pending = [];
  }

  toggleSessionRecording(_toState: 'on' | 'off') {
    // GTM has no session recording equivalent; intentional no-op.
  }

  private canPush(): boolean {
    return this.initialized && this.consent === 'granted';
  }

  /**
   * Pushes now under a grant, drops under a denial, waits while consent
   * is unresolved. The event's values are captured before the wait, so a
   * replayed event carries what it carried when it happened.
   */
  private dispatch(push: () => void) {
    if (!this.initialized || this.consent === 'denied') return;
    if (this.consent === 'granted') {
      push();
      return;
    }
    if (this.pending.length >= MAX_PENDING_EVENTS) return;
    this.pending.push(push);
  }

  private pushEvent(
    event: 'rio.track' | 'rio.page',
    name: string,
    properties?: Record<string, unknown>,
  ) {
    if (!this.initialized) return;
    const { eventId, ...rest } = stripPiiKeys(properties) ?? {};
    // `Record<GtmPerEventKey, ...>` requires a value for each per-event
    // key, so a key added to the set is a type error here until this
    // event supplies one.
    const values: Record<GtmPerEventKey, unknown> = {
      name,
      eventId: typeof eventId === 'string' ? eventId : undefined,
      properties: Object.keys(rest).length > 0 ? rest : undefined,
    };
    // Two entries, in this order. The merge that folds `rio.properties`
    // into the previous event's object cannot be undone from inside the
    // push that causes it, so the clear goes out first as an entry of its
    // own — with no `event` key, which is what keeps it from firing a
    // trigger — and the values land on a model holding nothing else.
    // Both are one dispatch, so a held event replays as the pair.
    this.dispatch(() => {
      const dataLayer = getOrCreateDataLayer();
      dataLayer.push({ rio: buildPerEventRio(() => null) });
      dataLayer.push({ event, rio: buildPerEventRio((key) => values[key]) });
    });
  }
}

/**
 * Builds the `rio` payload of a single entry, key by key from
 * `GTM_PER_EVENT_KEYS`, so an event's clear entry and its values entry
 * always cover the same keys.
 */
function buildPerEventRio(
  valueFor: (key: GtmPerEventKey) => unknown,
): Record<string, unknown> {
  const rio: Record<string, unknown> = {};
  for (const key of GTM_PER_EVENT_KEYS) rio[key] = valueFor(key);
  return rio;
}

/**
 * Returns only the allowlisted subset of identify traits. Keys keep our
 * camelCase (this is our own dataLayer vocabulary, not GA4's snake_case
 * convention).
 */
function filterTraitsForGtm(
  traits: Record<string, unknown> | undefined,
): Record<string, unknown> {
  if (!traits) return {};
  const result: Record<string, unknown> = {};
  for (const key of GTM_IDENTIFY_TRAIT_ALLOWLIST) {
    const value = traits[key];
    if (value === undefined || value === null) continue;
    result[key] = value;
  }
  return result;
}

/**
 * Factory: returns a `GtmDataLayerAdapter` when GTM is configured, else
 * `null`, and requests the container script on the way. The container id
 * is both the enable flag and the container to load — the same
 * empty-string kill-switch pattern as the GA adapter's measurement ID, so
 * unsetting the secret in an env drops both the feed and the container on
 * next page load with no redeploy.
 *
 * The two conditions differ deliberately: the feed runs wherever the id is
 * set, the loader only in production (see `loadGtmContainer`). Outside
 * production the entries queue unread, which is the behaviour this adapter
 * shipped with.
 *
 * The loader is also handed to the adapter rather than only called here:
 * this factory runs once per tab, and whether the container may load is a
 * property of the current path (see {@link GtmDataLayerAdapter.page}).
 */
export function createGtmDataLayerAdapter(env: {
  GOOGLE_TAG_MANAGER_CONTAINER_ID: string;
  ENVIRONMENT: string;
}): AnalyticsAdapter | null {
  if (!env.GOOGLE_TAG_MANAGER_CONTAINER_ID) return null;
  loadGtmContainer(env);
  return new GtmDataLayerAdapter(() => {
    loadGtmContainer(env);
  });
}
